import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Chip,
    Button,
    Divider,
    useTheme,
    Avatar,
    Tooltip,
    TextField,
    Switch,
    FormControlLabel,
    Autocomplete,
    CircularProgress,
    Snackbar,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CampaignIcon from "@mui/icons-material/Campaign";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import InfoIcon from "@mui/icons-material/Info";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CircleIcon from "@mui/icons-material/Circle";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { useNavigate } from "react-router-dom";
import {
    getNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    archiveNotification,
    pushOwnerNotification,
} from "../../../store/slices/notificationsSlice";
import { getJobs } from "../../../store/slices/jobsSlice";
import { getUser } from "../../../store/slices/userSlice";
import { getActiveUsersList } from "../../../store/slices/usersSlice";
import { isOwner } from "../../../utils/permissions";
import { formatUserDisplayName } from "../../../utils/userDisplayName";
import { routes } from "../../../utils/constants";
import JobDetailsDialog from "../jobs/JobDetailsDialog";

// Helper function to format relative time
const formatRelativeTime = (dateInput) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
};

// Get icon and color based on notification type
const getNotificationStyle = (type, isDarkMode) => {
    const styles = {
        job_technician_locked: {
            icon: <LockIcon />,
            color: isDarkMode ? "#fbbf24" : "#ff9800",
            bgColor: isDarkMode ? "rgba(251, 191, 36, 0.1)" : "rgba(255, 152, 0, 0.1)",
            label: "Locked",
        },
        job_technician_free_of_charge: {
            icon: <MoneyOffIcon />,
            color: isDarkMode ? "#2dd4bf" : "#0d9488",
            bgColor: isDarkMode ? "rgba(45, 212, 191, 0.12)" : "rgba(13, 148, 136, 0.08)",
            label: "Free of charge",
        },
        proposal_client_approved: {
            icon: <InfoIcon />,
            color: isDarkMode ? "#60a5fa" : "#2196f3",
            bgColor: isDarkMode ? "rgba(96, 165, 250, 0.1)" : "rgba(33, 150, 243, 0.1)",
            label: "Proposal",
        },
        info: {
            icon: <InfoIcon />,
            color: isDarkMode ? "#60a5fa" : "#2196f3",
            bgColor: isDarkMode ? "rgba(96, 165, 250, 0.1)" : "rgba(33, 150, 243, 0.1)",
            label: "Info",
        },
        owner_announcement: {
            icon: <CampaignIcon />,
            color: isDarkMode ? "#a78bfa" : "#7c3aed",
            bgColor: isDarkMode ? "rgba(167, 139, 250, 0.12)" : "rgba(124, 58, 237, 0.08)",
            label: "Announcement",
        },
        platform_announcement: {
            icon: <CampaignIcon />,
            color: isDarkMode ? "#38bdf8" : "#0284c7",
            bgColor: isDarkMode ? "rgba(56, 189, 248, 0.12)" : "rgba(2, 132, 199, 0.08)",
            label: "ServiceGeek",
        },
        daily_report: {
            icon: <AssessmentIcon />,
            color: isDarkMode ? "#818cf8" : "#4f46e5",
            bgColor: isDarkMode ? "rgba(129, 140, 248, 0.12)" : "rgba(79, 70, 229, 0.08)",
            label: "Daily report",
        },
        job_comment_mention: {
            icon: <AlternateEmailIcon />,
            color: isDarkMode ? "#34d399" : "#059669",
            bgColor: isDarkMode ? "rgba(52, 211, 153, 0.12)" : "rgba(5, 150, 105, 0.08)",
            label: "Mention",
        },
        job_done: {
            icon: <CheckCircleIcon />,
            color: isDarkMode ? "#4ade80" : "#16a34a",
            bgColor: isDarkMode ? "rgba(74, 222, 128, 0.12)" : "rgba(22, 163, 74, 0.08)",
            label: "Job Done",
        },
        job_no_technician: {
            icon: <PersonOffIcon />,
            color: isDarkMode ? "#fbbf24" : "#d97706",
            bgColor: isDarkMode ? "rgba(251, 191, 36, 0.12)" : "rgba(217, 119, 6, 0.08)",
            label: "No Technician",
        },
        job_unlock_request_dismissed: {
            icon: <LockIcon />,
            color: isDarkMode ? "#fbbf24" : "#ff9800",
            bgColor: isDarkMode ? "rgba(251, 191, 36, 0.1)" : "rgba(255, 152, 0, 0.1)",
            label: "Dismissed",
        },
        job_unlocked: {
            icon: <LockOpenIcon />,
            color: isDarkMode ? "#4ade80" : "#16a34a",
            bgColor: isDarkMode ? "rgba(74, 222, 128, 0.12)" : "rgba(22, 163, 74, 0.08)",
            label: "Unlocked",
        },
        client_request_rejected: {
            icon: <CancelIcon />,
            color: isDarkMode ? "#fca5a5" : "#dc2626",
            bgColor: isDarkMode ? "rgba(252, 165, 165, 0.12)" : "rgba(220, 38, 38, 0.08)",
            label: "Rejected",
        },
        client_request_accepted: {
            icon: <CheckCircleOutlineIcon />,
            color: isDarkMode ? "#4ade80" : "#16a34a",
            bgColor: isDarkMode ? "rgba(74, 222, 128, 0.12)" : "rgba(22, 163, 74, 0.08)",
            label: "Accepted",
        },
        client_request_submitted: {
            icon: <PersonAddAlt1Icon />,
            color: isDarkMode ? "#fbbf24" : "#d97706",
            bgColor: isDarkMode ? "rgba(251, 191, 36, 0.12)" : "rgba(217, 119, 6, 0.08)",
            label: "Client Request",
        },
        client_admin_assigned: {
            icon: <PersonAddAlt1Icon />,
            color: isDarkMode ? "#60a5fa" : "#2563eb",
            bgColor: isDarkMode ? "rgba(96, 165, 250, 0.12)" : "rgba(37, 99, 235, 0.08)",
            label: "Assigned",
        },
    };
    return styles[type] || styles.info;
};

// Get title based on notification type
const getNotificationTitle = (notification) => {
    switch (notification.type) {
        case 'job_technician_locked':
            return 'Technician Assignment Locked';
        case 'job_technician_free_of_charge':
            return 'Technician marked Free of charge';
        case 'proposal_client_approved':
            return 'Proposal Approved By Client';
        case 'owner_announcement':
            return 'Announcement';
        case 'platform_announcement':
            return 'Platform update';
        case 'daily_report': {
            const lines = String(notification.text || '')
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean)
                .filter((l) => l !== '[TEST]');
            return lines[0] || 'Daily report';
        }
        case 'job_comment_mention':
            return 'Mentioned in a comment';
        case 'job_done':
            return 'Job Marked Done';
        case 'job_no_technician':
            return 'No Technician Assigned';
        case 'job_unlock_request_dismissed':
            return 'Unlock Request Dismissed';
        case 'job_unlocked':
            return 'Job Unlocked';
        case 'client_request_rejected':
            return 'Client Request Rejected';
        case 'client_request_accepted':
            return 'Client Request Accepted';
        case 'client_request_submitted':
            return 'Client Request';
        case 'client_admin_assigned':
            return 'Assigned to Client';
        default:
            return 'Notification';
    }
};

// Get the initial tab value for JobDetailsDialog based on notification type
const getInitialTabForNotification = (notification) => {
    switch (notification.type) {
        case 'job_technician_locked':
        case 'job_technician_free_of_charge':
            return 1; // Technicians tab
        case 'proposal_client_approved':
            return 4; // Proposal tab
        case 'job_comment_mention':
            return 6; // Comments tab
        case 'job_done':
        case 'job_no_technician':
            return 0; // Details tab
        default:
            return 0; // Details tab
    }
};

export default function Notifications() {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const { items: notifications, unreadCount } = useSelector(getNotifications);
    const { jobs } = useSelector(getJobs);
    const user = useSelector(getUser);
    const usersList = useSelector(getActiveUsersList);
    const showOwnerComposer = isOwner(user);

    // State for JobDetailsDialog
    const [selectedJob, setSelectedJob] = useState(null);
    const [initialJobDetailsTabValue, setInitialJobDetailsTabValue] = useState(0);

    const [announcementText, setAnnouncementText] = useState("");
    const [broadcastAll, setBroadcastAll] = useState(false);
    const [announcementRecipients, setAnnouncementRecipients] = useState([]);
    const [pushSending, setPushSending] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [composerExpanded, setComposerExpanded] = useState(false);
    const [detailNotification, setDetailNotification] = useState(null);

    const handleMarkAsRead = (notification) => {
        if (!notification.read_at) {
            dispatch(markNotificationRead(notification.id));
        }
    };

    const handleMarkAllAsRead = () => {
        dispatch(markAllNotificationsRead());
    };

    const handleDelete = (id) => {
        dispatch(archiveNotification(id));
    };

    const handleOpenJob = (notification) => {
        // Find the job in Redux store and open the modal
        if (notification.job_id && jobs) {
            const job = jobs.find(j => j.id === notification.job_id);
            if (job) {
                // Set the initial tab based on notification type
                setInitialJobDetailsTabValue(getInitialTabForNotification(notification));
                setSelectedJob(job);
            }
        }
    };

    const notificationHasJob = (notification) =>
        Boolean(notification.job_id && jobs?.find((j) => j.id === notification.job_id));

    /** Job open / client-request navigate — everything else opens the detail modal. */
    const notificationHasSpecialClick = (notification) =>
        notificationHasJob(notification)
        || notification.type === 'client_request_submitted';

    const handleNotificationClick = (notification) => {
        handleMarkAsRead(notification);
        if (notificationHasJob(notification)) {
            handleOpenJob(notification);
            return;
        }
        if (notification.type === 'client_request_submitted') {
            navigate(routes.clientRequests);
            return;
        }
        setDetailNotification(notification);
    };

    const handleCloseDetail = () => setDetailNotification(null);

    const handleSendAnnouncement = async () => {
        const text = announcementText.trim();
        if (!text) return;
        if (!broadcastAll && announcementRecipients.length === 0) return;
        setPushSending(true);
        try {
            const payload = broadcastAll
                ? { text, broadcast: true }
                : { text, userIds: announcementRecipients.map((u) => u.id) };
            const data = await dispatch(pushOwnerNotification(payload)).unwrap();
            setSnackbar({
                open: true,
                message: `Sent to ${data.recipientCount} recipient${data.recipientCount === 1 ? "" : "s"}.`,
                severity: "success",
            });
            setAnnouncementText("");
            setAnnouncementRecipients([]);
            setBroadcastAll(false);
            setComposerExpanded(false);
        } catch (e) {
            setSnackbar({
                open: true,
                message: typeof e === "string" ? e : "Could not send announcement.",
                severity: "error",
            });
        } finally {
            setPushSending(false);
        }
    };

    const canSendAnnouncement =
        announcementText.trim().length > 0 &&
        (broadcastAll || announcementRecipients.length > 0);

    return (
        <Box
            sx={{
                height: "100%",
                overflow: "auto",
                backgroundColor: theme.palette.background.default,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.75, sm: 2.5 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "center" },
                        gap: { xs: 1.5, sm: 0 },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: isDarkMode
                                    ? theme.palette.primary.main
                                    : "#000",
                                width: 44,
                                height: 44,
                            }}
                        >
                            <NotificationsIcon />
                        </Avatar>
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                                }}
                            >
                                Notifications
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                                }}
                            >
                                {unreadCount > 0
                                    ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
                                    : "All caught up!"}
                            </Typography>
                        </Box>
                    </Box>
                    {unreadCount > 0 && (
                        <Button
                            startIcon={<DoneAllIcon />}
                            onClick={handleMarkAllAsRead}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontFamily: '"Space Grotesk", Inter, sans-serif',
                                color: isDarkMode
                                    ? theme.palette.primary.main
                                    : "#000",
                                "&:hover": {
                                    backgroundColor: isDarkMode
                                        ? "rgba(59, 130, 246, 0.1)"
                                        : "rgba(0, 0, 0, 0.04)",
                                },
                            }}
                        >
                            Mark all as read
                        </Button>
                    )}
                </Box>
            </Box>

            {showOwnerComposer && (
                <Accordion
                    expanded={composerExpanded}
                    onChange={(_, expanded) => setComposerExpanded(expanded)}
                    elevation={0}
                    disableGutters
                    sx={{
                        mx: 2,
                        mt: 1.5,
                        mb: 0.5,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                        overflow: "hidden",
                        boxShadow: isDarkMode
                            ? "0 8px 32px rgba(0,0,0,0.35)"
                            : "0 8px 30px rgba(15, 23, 42, 0.08)",
                        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                        "&:before": { display: "none" },
                        ...(composerExpanded && {
                            borderColor: isDarkMode ? "rgba(167, 139, 250, 0.35)" : "rgba(124, 58, 237, 0.22)",
                        }),
                    }}
                >
                    <AccordionSummary
                        expandIcon={
                            <ExpandMoreIcon sx={{ color: theme.palette.text.secondary }} />
                        }
                        sx={{
                            px: 2,
                            py: 1.25,
                            minHeight: 64,
                            "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                gap: 2,
                                my: 1,
                            },
                            "&:hover": {
                                bgcolor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                background: isDarkMode
                                    ? "linear-gradient(145deg, rgba(167,139,250,0.28) 0%, rgba(99,102,241,0.12) 100%)"
                                    : "linear-gradient(145deg, rgba(124,58,237,0.14) 0%, rgba(99,102,241,0.08) 100%)",
                                color: isDarkMode ? "#ddd6fe" : "#5b21b6",
                            }}
                        >
                            <CampaignIcon sx={{ fontSize: 26 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 700,
                                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                Compose announcement
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                                    display: "block",
                                    mt: 0.25,
                                }}
                            >
                                {composerExpanded
                                    ? "Team members see this in their inbox"
                                    : "Tap to push an in-app message to your team"}
                            </Typography>
                        </Box>
                        {canSendAnnouncement && (
                            <Chip
                                label="Ready to send"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{
                                    display: { xs: "none", sm: "flex" },
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    height: 26,
                                }}
                            />
                        )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pt: 0, pb: 2.5 }}>
                        <Stack spacing={2.25}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={10}
                                label="Message"
                                placeholder="Write something your team should see…"
                                value={announcementText}
                                onChange={(e) => setAnnouncementText(e.target.value)}
                                inputProps={{ maxLength: 4000 }}
                                helperText={`${announcementText.length.toLocaleString()} / 4,000`}
                                variant="outlined"
                                size="small"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                                        lineHeight: 1.5,
                                    },
                                    "& .MuiInputLabel-root": {
                                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                                    },
                                }}
                            />

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    alignItems: { xs: "stretch", sm: "center" },
                                    justifyContent: "space-between",
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontFamily: '"Space Grotesk", Inter, sans-serif',
                                            display: "block",
                                            mb: 0.5,
                                        }}
                                    >
                                        Audience
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={broadcastAll}
                                                onChange={(e) => setBroadcastAll(e.target.checked)}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: '"Space Grotesk", Inter, sans-serif' }}
                                            >
                                                Everyone (broadcast)
                                            </Typography>
                                        }
                                    />
                                </Box>
                            </Box>

                            {!broadcastAll && (
                                <Autocomplete
                                    multiple
                                    options={usersList}
                                    value={announcementRecipients}
                                    onChange={(_, v) => setAnnouncementRecipients(v)}
                                    getOptionLabel={(opt) => formatUserDisplayName(opt) || String(opt?.id ?? "")}
                                    filterOptions={(options, { inputValue }) => {
                                        const q = inputValue.trim().toLowerCase();
                                        if (!q) return options;
                                        return options.filter((opt) => {
                                            const haystack = [
                                                formatUserDisplayName(opt),
                                                opt.first_name,
                                                opt.last_name,
                                                opt.username,
                                                opt.email,
                                            ]
                                                .filter(Boolean)
                                                .join(" ")
                                                .toLowerCase();
                                            return haystack.includes(q);
                                        });
                                    }}
                                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Recipients"
                                            placeholder="Search by name or email"
                                            size="small"
                                            sx={{
                                                "& .MuiInputBase-input": {
                                                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            )}

                            {broadcastAll && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: '"Space Grotesk", Inter, sans-serif', lineHeight: 1.6 }}
                                >
                                    Sends to every user account except yours. Recipients are resolved on the server;
                                </Typography>
                            )}

                            {!broadcastAll && usersList.length === 0 && (
                                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ fontFamily: '"Space Grotesk", Inter, sans-serif' }}>
                                        User list is empty. Open Clients (or another section that loads team data), then
                                        return here to select recipients.
                                    </Typography>
                                </Alert>
                            )}

                            <Divider sx={{ opacity: 0.6 }} />

                            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1.5}>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    disabled={pushSending}
                                    onClick={() => {
                                        setAnnouncementText("");
                                        setAnnouncementRecipients([]);
                                        setBroadcastAll(false);
                                    }}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                                        borderRadius: 2,
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    disabled={!canSendAnnouncement || pushSending}
                                    onClick={handleSendAnnouncement}
                                    startIcon={
                                        pushSending ? (
                                            <CircularProgress size={18} color="inherit" />
                                        ) : (
                                            <SendIcon sx={{ fontSize: 20 }} />
                                        )
                                    }
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                                        borderRadius: 2,
                                        px: 2.5,
                                        boxShadow: "none",
                                        color: "#fff",
                                        background: isDarkMode
                                            ? "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)"
                                            : "linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)",
                                        "&:hover": {
                                            boxShadow: isDarkMode
                                                ? "0 4px 20px rgba(124,58,237,0.35)"
                                                : "0 4px 20px rgba(79,70,229,0.35)",
                                        },
                                        "&.Mui-disabled": {
                                            color: "rgba(255, 255, 255, 0.7)",
                                            background: isDarkMode
                                                ? "linear-gradient(135deg, rgba(124,58,237,0.45) 0%, rgba(99,102,241,0.45) 100%)"
                                                : "linear-gradient(135deg, rgba(109,40,217,0.45) 0%, rgba(79,70,229,0.45) 100%)",
                                        },
                                    }}
                                >
                                    {pushSending ? "Sending…" : "Send announcement"}
                                </Button>
                            </Stack>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Notifications List */}
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                {notifications.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 8,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        <NotificationsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: '"Space Grotesk", Inter, sans-serif',
                                fontWeight: 600,
                            }}
                        >
                            No notifications
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: '"Space Grotesk", Inter, sans-serif' }}
                        >
                            You're all caught up!
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {notifications.map((notification, index) => {
                            const style = getNotificationStyle(notification.type, isDarkMode);
                            const isRead = !!notification.read_at;
                            const title = getNotificationTitle(notification);
                            const hasJob = notification.job_id && jobs?.find(j => j.id === notification.job_id);
                            const isDailyReport = notification.type === 'daily_report';
                            const preserveNewlines =
                                isDailyReport
                                || notification.type === 'platform_announcement'
                                || notification.type === 'owner_announcement';
                            const opensDetailModal = !notificationHasSpecialClick(notification);
                            
                            return (
                                <React.Fragment key={notification.id}>
                                    <ListItem
                                        onClick={() => handleNotificationClick(notification)}
                                        sx={{
                                            borderRadius: 2.5,
                                            mb: 1.25,
                                            background: !isRead
                                                ? `linear-gradient(135deg, ${style.bgColor} 0%, rgba(148, 163, 184, 0.05) 100%)`
                                                : theme.palette.background.paper,
                                            borderLeft: `3px solid ${
                                                isRead
                                                    ? (isDarkMode
                                                        ? theme.palette.divider
                                                        : theme.palette.grey[300])
                                                    : style.color
                                            }`,
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            boxShadow: isRead
                                                ? "0 6px 12px rgba(15,23,42,0.04)"
                                                : "0 10px 30px rgba(15,23,42,0.16)",
                                            position: "relative",
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                backgroundColor: hasJob || opensDetailModal
                                                    ? (isDarkMode
                                                        ? "rgba(30, 64, 175, 0.18)"
                                                        : "rgba(59, 130, 246, 0.06)")
                                                    : undefined,
                                                boxShadow: hasJob || opensDetailModal
                                                    ? "0 14px 40px rgba(15,23,42,0.22)"
                                                    : "0 10px 30px rgba(15,23,42,0.16)",
                                            },
                                            alignItems: "flex-start",
                                            py: { xs: 1.5, sm: 2 },
                                            px: { xs: 1.5, sm: 2 },
                                        }}
                                        secondaryAction={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {!isRead && (
                                                    <>
                                                        <Tooltip title="Unread">
                                                            <CircleIcon
                                                                sx={{
                                                                    fontSize: 10,
                                                                    color: style.color,
                                                                }}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="Mark as read">
                                                            <IconButton
                                                                edge="end"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMarkAsRead(notification);
                                                                }}
                                                                sx={{
                                                                    color: theme.palette.primary.main,
                                                                    "&:hover": {
                                                                        backgroundColor: isDarkMode
                                                                            ? "rgba(59, 130, 246, 0.12)"
                                                                            : "rgba(25, 118, 210, 0.08)",
                                                                    },
                                                                }}
                                                            >
                                                                <MarkEmailReadIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(notification.id);
                                                        }}
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            "&:hover": {
                                                                color: theme.palette.error.main,
                                                                backgroundColor: isDarkMode
                                                                    ? "rgba(248, 113, 113, 0.1)"
                                                                    : "rgba(244, 67, 54, 0.1)",
                                                            },
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        }
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 44,
                                                color: style.color,
                                                mt: 0.5,
                                            }}
                                        >
                                            {style.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        justifyContent: "space-between",
                                                        gap: 1.5,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: isRead ? 500 : 700,
                                                                color: theme.palette.text.primary,
                                                                fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                                fontSize: "0.95rem",
                                                                whiteSpace: "nowrap",
                                                                textOverflow: "ellipsis",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {title}
                                                        </Typography>
                                                        <Chip
                                                            label={style.label}
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: "0.65rem",
                                                                fontWeight: 600,
                                                                textTransform: "uppercase",
                                                                backgroundColor: style.bgColor,
                                                                color: style.color,
                                                                border: `1px solid ${style.color}`,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: theme.palette.text.disabled,
                                                            fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                        }}
                                                    >
                                                        {formatRelativeTime(notification.created_at)}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        component="div"
                                                        variant="body2"
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                            mb: 0.5,
                                                            pr: 8,
                                                            ...(preserveNewlines
                                                                ? {
                                                                    whiteSpace: "pre-wrap",
                                                                    lineHeight: 1.55,
                                                                }
                                                                : {}),
                                                            ...(isDailyReport
                                                                ? {
                                                                    fontSize: "0.875rem",
                                                                    letterSpacing: "-0.01em",
                                                                    mt: 0.5,
                                                                    p: 1.5,
                                                                    borderRadius: 1.5,
                                                                    backgroundColor: isDarkMode
                                                                        ? "rgba(15, 23, 42, 0.45)"
                                                                        : "rgba(248, 250, 252, 0.95)",
                                                                    border: `1px solid ${theme.palette.divider}`,
                                                                }
                                                                : {}),
                                                        }}
                                                    >
                                                        {notification.text}
                                                    </Typography>
                                                    {isDailyReport && isOwner(user) && (
                                                        <Button
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMarkAsRead(notification);
                                                                navigate(routes.alerts);
                                                            }}
                                                            sx={{
                                                                alignSelf: "flex-start",
                                                                textTransform: "none",
                                                                fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                                fontWeight: 600,
                                                                color: style.color,
                                                                px: 0,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            Manage alerts
                                                        </Button>
                                                    )}
                                                    {(notification.job_work_order || notification.job_description) && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.secondary,
                                                                fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                                display: 'block',
                                                                mb: 0.25,
                                                                pr: 8,
                                                            }}
                                                        >
                                                            {notification.job_work_order && (
                                                                <>
                                                                    WO: {notification.job_work_order}
                                                                    {notification.job_description && ' • '}
                                                                </>
                                                            )}
                                                            {notification.job_description && notification.job_description}
                                                        </Typography>
                                                    )}
                                                    {notification.technician_name && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.secondary,
                                                                fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                                display: 'block',
                                                                mb: 0.5,
                                                                pr: 8,
                                                            }}
                                                        >
                                                            Technician/Vendor: {notification.technician_name}
                                                        </Typography>
                                                    )}
                                                    {notification.actor && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.disabled,
                                                                fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                                display: 'block',
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            By: {notification.actor}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < notifications.length - 1 && (
                                        <Divider sx={{ my: 0.5, opacity: 0.5 }} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </Box>

            <Dialog
                open={Boolean(detailNotification)}
                onClose={handleCloseDetail}
                fullWidth
                maxWidth="sm"
                aria-labelledby="notification-detail-title"
            >
                {detailNotification && (() => {
                    const detailStyle = getNotificationStyle(detailNotification.type, isDarkMode);
                    const detailTitle = getNotificationTitle(detailNotification);
                    const detailPreserveNewlines =
                        detailNotification.type === 'daily_report'
                        || detailNotification.type === 'platform_announcement'
                        || detailNotification.type === 'owner_announcement';
                    return (
                        <>
                            <DialogTitle id="notification-detail-title" sx={{ pr: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                    <Box sx={{ color: detailStyle.color, mt: 0.25 }}>
                                        {detailStyle.icon}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontFamily: '"Space Grotesk", Inter, sans-serif',
                                                    fontSize: "1.15rem",
                                                }}
                                            >
                                                {detailTitle}
                                            </Typography>
                                            <Chip
                                                label={detailStyle.label}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontSize: "0.7rem",
                                                    fontWeight: 600,
                                                    textTransform: "uppercase",
                                                    backgroundColor: detailStyle.bgColor,
                                                    color: detailStyle.color,
                                                    border: `1px solid ${detailStyle.color}`,
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: theme.palette.text.disabled,
                                                fontFamily: '"Space Grotesk", Inter, sans-serif',
                                            }}
                                        >
                                            {formatRelativeTime(detailNotification.created_at)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </DialogTitle>
                            <DialogContent dividers>
                                <Typography
                                    component="div"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                                        fontSize: "1.05rem",
                                        lineHeight: 1.65,
                                        ...(detailPreserveNewlines
                                            ? { whiteSpace: "pre-wrap" }
                                            : {}),
                                    }}
                                >
                                    {detailNotification.text}
                                </Typography>
                                {(detailNotification.job_work_order || detailNotification.job_description) && (
                                    <Typography
                                        sx={{
                                            mt: 2,
                                            color: theme.palette.text.secondary,
                                            fontFamily: '"Space Grotesk", Inter, sans-serif',
                                            fontSize: "0.95rem",
                                        }}
                                    >
                                        {detailNotification.job_work_order && (
                                            <>
                                                WO: {detailNotification.job_work_order}
                                                {detailNotification.job_description && ' • '}
                                            </>
                                        )}
                                        {detailNotification.job_description}
                                    </Typography>
                                )}
                                {detailNotification.technician_name && (
                                    <Typography
                                        sx={{
                                            mt: 1,
                                            color: theme.palette.text.secondary,
                                            fontFamily: '"Space Grotesk", Inter, sans-serif',
                                            fontSize: "0.95rem",
                                        }}
                                    >
                                        Technician/Vendor: {detailNotification.technician_name}
                                    </Typography>
                                )}
                                {detailNotification.actor && (
                                    <Typography
                                        sx={{
                                            mt: 1,
                                            color: theme.palette.text.disabled,
                                            fontFamily: '"Space Grotesk", Inter, sans-serif',
                                            fontSize: "0.9rem",
                                        }}
                                    >
                                        By: {detailNotification.actor}
                                    </Typography>
                                )}
                                {detailNotification.type === 'daily_report' && isOwner(user) && (
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            handleCloseDetail();
                                            navigate(routes.alerts);
                                        }}
                                        sx={{
                                            mt: 2,
                                            textTransform: "none",
                                            fontFamily: '"Space Grotesk", Inter, sans-serif',
                                            fontWeight: 600,
                                            color: detailStyle.color,
                                            px: 0,
                                            minWidth: 0,
                                        }}
                                    >
                                        Manage alerts
                                    </Button>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDetail} sx={{ textTransform: "none" }}>
                                    Close
                                </Button>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>

            {/* Job Details Dialog */}
            <JobDetailsDialog
                selectedJob={selectedJob}
                setSelectedJob={setSelectedJob}
                user={user}
                initialTabValue={initialJobDetailsTabValue}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
